import React, { useEffect, useState, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'
import {
  PageWrapper,
  DashboardCard,
  DashboardHeader,
  DashboardTitle,
  HeaderActions,
  StatusBadge,
  SubmitButton,
  TabContent,
  BoldText,
  LoadingWrapper
} from './style'
import Tabbar from './components/Tabbar'
import StateTab from './components/StateTab'
import PressureTab from './components/PressureTab'
import ResponseTab from './components/ResponseTab'
import EvaluationTab from './components/EvaluationTab'
import AlertIconCircle from '@/assets/icons/alert-icon-circle'
import SuccessIcon from '@/assets/icons/success-icon'
import { getMonitoringDashboard, monitoringFinishPropose } from 'api/monitoring'
import Loader from 'components/Loader'
import { MonitoringContext } from 'providers/monitoring'
import Modal from 'components/Modal'
import ValidationProposalModal from '../../components/Footer/components/ValidationProposalModal'
import SubmitConfirmationModal from '../../components/Footer/components/SubmitConfirmationModal'
import PressureValidationModal from '../../components/Footer/components/pressureValidationModal'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import {
  canEvaluationBeSubmitted,
  getPressureProgress,
  getSiteResponseProgress,
  getUncompletedSection,
  isSectionComplete
} from './utils'
import { TABS } from './constants'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'

const renderTabContent = (activeTab, monitoring, tabSummary) => {
  const isDraft = monitoring?.monitoring_status === SITE_STATUS_DRAFT_ID
  switch (activeTab) {
    case TABS.STATE:
      return <StateTab monitoring={monitoring} stateStatus={tabSummary?.state} isDraft={isDraft} />
    case TABS.PRESSURE:
      return <PressureTab monitoring={monitoring} pressureStatus={tabSummary?.pressure} isDraft={isDraft} />
    case TABS.RESPONSE:
      return (
        <ResponseTab
          response={monitoring?.site_responses?.[0]}
          responseStatus={tabSummary?.response}
          isDraft={isDraft}
          monitoringId={monitoring?.id}
        />
      )
    case TABS.EVALUATION:
      return <EvaluationTab monitoring={monitoring} />
    default:
      return null
  }
}

const MonitoringDashboardPage = () => {
  const params = useParams()
  const history = useNavigate()
  const { monitoringProposalFieldValidation, getMonitoringValidation, mergedParams, checkPressureThreatsCompleted } =
    useContext(MonitoringContext)
  const [activeTab, setActiveTab] = useState(TABS.STATE)
  const [monitoring, setMonitoring] = useState({})
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false)
  const [isSubmitPressureModalOpen, setIsSubmitPressureModalOpen] = useState(false)
  const sections = getUncompletedSection(monitoringProposalFieldValidation, monitoring)

  function goBack() {
    history('/sites')
  }

  useEffect(() => {
    setLoading(true)
    const fetchMonitoringData = async () => {
      try {
        const monitoringId = params?.monitoringId || ''
        if (!monitoringId) {
          setLoading(false)
          return
        }
        const res = await getMonitoringDashboard(monitoringId)
        setMonitoring(res)
      } catch (error) {
        console.error('Error fetching monitoring data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchMonitoringData()
  }, [params?.monitoringId])

  useEffect(() => {
    if (!monitoring || Object.keys(monitoring).length === 0) return
    const isCurrentTabVisible =
      (activeTab === TABS.STATE && monitoring?.state_evaluation === true) ||
      (activeTab === TABS.PRESSURE && monitoring?.pressure_evaluation === true) ||
      (activeTab === TABS.RESPONSE && monitoring?.response_evaluation === true) ||
      activeTab === TABS.EVALUATION

    if (!isCurrentTabVisible) {
      if (monitoring?.state_evaluation === true) setActiveTab(TABS.STATE)
      else if (monitoring?.pressure_evaluation === true) setActiveTab(TABS.PRESSURE)
      else if (monitoring?.response_evaluation === true) setActiveTab(TABS.RESPONSE)
      else setActiveTab(TABS.EVALUATION)
    }
  }, [monitoring, activeTab])

  const totalStateItems = monitoring?.biodiversity?.totalItems || 0
  const speciesBenchmark = monitoring?.biodiversity?.speciesBenchmarkCounts
  const ecoBenchmark = monitoring?.biodiversity?.ecoBenchmarkCounts
  const stateStarted =
    monitoring?.biodiversity?.totalBenchmarkCounts?.started ??
    ((speciesBenchmark?.started || 0) + (ecoBenchmark?.started || 0))
  const stateCompleted =
    monitoring?.biodiversity?.totalBenchmarkCounts?.completed ??
    ((speciesBenchmark?.completed || 0) + (ecoBenchmark?.completed || 0))
  const stateProgressPercent =
    totalStateItems > 0 ? Math.min(100, Math.round(((stateStarted + stateCompleted) / totalStateItems) * 100)) : 0
  const canSubmitState = stateCompleted > 0 && stateStarted === 0

  const pressureProgressPercent = getPressureProgress(monitoring)
  const pressureTotal = monitoring?.pressureBiodiversity?.pressuresTotal || 0
  const canSubmitPressure =
    pressureTotal > 0 &&
    monitoring?.pressureBiodiversity?.pressuresWithTriggersAdded === pressureTotal

  const responseProgressPercent = getSiteResponseProgress(monitoring?.site_responses?.[0])
  const canSubmitResponse = monitoring?.isSiteResponseValidated && responseProgressPercent === 100

  const completedSections = []
  if (monitoring?.state_evaluation && monitoringProposalFieldValidation?.siteState?.result) {
    completedSections.push('State')
  }
  if (monitoring?.pressure_evaluation && monitoringProposalFieldValidation?.sitePressure?.result) {
    completedSections.push('Pressure')
  }
  if (monitoring?.response_evaluation && isSectionComplete(monitoringProposalFieldValidation?.siteResponse)) {
    completedSections.push('Response')
  }

  const handleSubmitDialog = async () => {
    try {
      if (checkPressureThreatsCompleted) {
        setIsSubmitPressureModalOpen(true)
      } else {
        const validateRes = await getMonitoringValidation()
        const freshSections = getUncompletedSection(validateRes, monitoring)
        if (freshSections.length > 0) {
          setIsModalOpen(true)
        } else {
          setIsSubmitModalOpen(true)
        }
      }
    } catch (error) {
      console.error('Validation failed:', error)
      showErrorToast('An error occurred during validation. Please try again.', 'showBorder')
    }
  }

  const handleSubmit = async () => {
    try {
      await monitoringFinishPropose(monitoring.id)
      showSuccessToast('Monitoring evaluation proposed successfully', 'showBorder')
      goBack()
    } catch (error) {
      console.error('Submission failed:', error)
      showErrorToast('An error occurred during submission. Please try again.', 'showBorder')
    }
  }

  const tabSummary = {
    state: { progress: stateProgressPercent || 0, status: canSubmitState },
    pressure: { progress: pressureProgressPercent || 0, status: canSubmitPressure },
    response: {
      progress: responseProgressPercent || 0,
      status: canSubmitResponse
    }
  }

  return (
    <div>
      <PageWrapper>
        <DashboardCard>
          <DashboardHeader>
            <DashboardTitle>
              <FormattedMessage id="monitoringEvaluationStatus" defaultMessage="Monitoring evaluation status" />
            </DashboardTitle>
            {!loading && monitoring.monitoring_status === SITE_STATUS_DRAFT_ID && (
              <HeaderActions>
                <StatusBadge tone={canEvaluationBeSubmitted(monitoring) ? 'success' : 'error'}>
                  {canEvaluationBeSubmitted(monitoring) ? <SuccessIcon /> : <AlertIconCircle />}{' '}
                  <FormattedMessage
                    id="monitoringCanBeSubmitted"
                    defaultMessage="Evaluation can be submitted: {value}"
                    values={{
                      value: canEvaluationBeSubmitted(monitoring) ? <BoldText>YES</BoldText> : <BoldText>NO</BoldText>
                    }}
                  />
                </StatusBadge>
                <SubmitButton
                  type="button"
                  onClick={handleSubmitDialog}
                >
                  <FormattedMessage id="monitoringSubmitEvaluation" defaultMessage="Submit evaluation" />
                </SubmitButton>
              </HeaderActions>
            )}
          </DashboardHeader>

          {loading ? (
            <LoadingWrapper>
              <Loader />
            </LoadingWrapper>
          ) : (
            <>
              <Tabbar
                isDraft={monitoring.monitoring_status === SITE_STATUS_DRAFT_ID}
                activeTab={activeTab}
                onChangeTab={setActiveTab}
                tabSummary={tabSummary}
                monitoring={monitoring}
              />
              <TabContent>{renderTabContent(activeTab, monitoring, tabSummary)}</TabContent>
            </>
          )}
        </DashboardCard>
      </PageWrapper>

      <Modal
        width="416px"
        toggleModal={setIsModalOpen}
        isOpen={isModalOpen && sections.length > 0}
        modalTitleId="unfinishedProposal"
        zIndex={99999}
      >
        <ValidationProposalModal sections={sections} toggleModal={setIsModalOpen} />
      </Modal>
      <Modal
        width="416px"
        toggleModal={setIsSubmitModalOpen}
        isOpen={isSubmitModalOpen && sections.length === 0}
        modalTitleId="submitEvaluation"
        zIndex={99999}
      >
        <SubmitConfirmationModal
          onBack={() => setIsSubmitModalOpen(false)}
          onSubmit={handleSubmit}
          completedSections={completedSections}
          monitoring={monitoring}
        />
      </Modal>
      <Modal
        width="416px"
        toggleModal={setIsSubmitPressureModalOpen}
        isOpen={isSubmitPressureModalOpen}
        modalTitleId="unfinishedProposal"
        zIndex={99999}
      >
        <PressureValidationModal toggleModal={() => setIsSubmitPressureModalOpen(false)} />
      </Modal>
    </div>
  )
}

export default MonitoringDashboardPage
